import { calculatePercentageChangeFast, getDate } from '@einhaus/meteoric-helpers';
import type { PortfolioBacktest } from '@einhaus/project314-types';
import type { PortfolioBacktestColumnId } from './portfolioBacktestColumnConfig';
import { summarizeFrozenCapital } from './portfolioBacktestFrozenSnapshot';
import { formatBacktestPercent } from './portfolioBacktestNumberFormatters';
import { calculatePortfolioCagr } from '@/utils/backtestHelpers';
import { portfolioEvaluationWindowWarning } from '../PortfolioBacktestStats/portfolioWalkForwardBoundary';

const formatOptionalPercent = (value: number | null | undefined): string => (value == null ? '-' : formatBacktestPercent(value));

/**
 * Comparable value per column, used only to decide whether a column separates the visible rows.
 *
 * Each entry mirrors what the cell actually renders, not the raw field: start dates differ in their
 * stored seconds while displaying identically, and rounded percentages collapse to the same text. A
 * column the user sees as uniform has to be reported as uniform. `id` is omitted because it is
 * unique by definition.
 */
const uniformityValueByColumn: Partial<Record<PortfolioBacktestColumnId, (backtest: PortfolioBacktest) => string>> = {
    capital: summarizeFrozenCapital,
    status: (backtest) => backtest.status,
    numTrades: (backtest) => backtest.numTrades.toLocaleString(),
    startingBalance: (backtest) => backtest.startingBalance.toLocaleString(),
    adjustedEndingBalance: (backtest) => (backtest.endingBalance - backtest.replenishmentAmount).toLocaleString(),
    replenishmentCount: (backtest) => backtest.replenishmentCount.toLocaleString(),
    replenishmentAmount: (backtest) => backtest.replenishmentAmount.toLocaleString(),
    percentageChange: (backtest) =>
        formatBacktestPercent(
            calculatePercentageChangeFast(backtest.startingBalance, backtest.endingBalance - backtest.replenishmentAmount)
        ),
    returnOnRiskCapital: (backtest) => formatBacktestPercent(backtest.returnOnRiskCapital),
    cagr: (backtest) => {
        const value = calculatePortfolioCagr(backtest);
        return value === null ? '—' : `${value.toFixed(2)}%`;
    },
    sharpeRatio: (backtest) => (backtest.sharpeRatio ? backtest.sharpeRatio.toFixed(2) : '-'),
    maxDrawdown: (backtest) => formatOptionalPercent(backtest.maxDrawdown),
    spyReturnPercent: (backtest) => formatOptionalPercent(backtest.spyReturnPercent),
    universeBuyHoldReturnPercent: (backtest) => formatOptionalPercent(backtest.universeBuyHoldReturnPercent),
    timingEdgePercent: (backtest) => formatOptionalPercent(backtest.timingEdgePercent),
    walkForward: (backtest) => {
        const forwardEvaluation = backtest.diagnostics?.forwardEvaluation;

        if (!forwardEvaluation?.portfolio.walkForward) return '-';
        const warning = portfolioEvaluationWindowWarning({ ...backtest, trainingEndDateTime: forwardEvaluation.trainingEndDateTime });
        return `${warning?.label ?? forwardEvaluation.verdict.status}|${formatOptionalPercent(forwardEvaluation.portfolio.walkForward.avgProfitPercent)}|${forwardEvaluation.portfolio.walkForward.tradeCount}`;
    },
    startDateTime: (backtest) => getDate({ date: backtest.startDateTime, format: 'readable2' }),
    endDateTime: (backtest) => getDate({ date: backtest.endDateTime, format: 'readable2' })
};

/**
 * Columns that hold the same value on every currently visible row, and so cost width without
 * separating anything. Recomputed against the filtered rows, so a column becomes uniform (or stops
 * being uniform) as the user narrows the table.
 */
export const findUniformColumnIds = (backtests: readonly PortfolioBacktest[]): Set<PortfolioBacktestColumnId> => {
    const uniformColumnIds = new Set<PortfolioBacktestColumnId>();

    // A single row makes every column trivially uniform, which is not a useful thing to report.
    if (backtests.length < 2) {
        return uniformColumnIds;
    }

    const [firstBacktest] = backtests;

    if (!firstBacktest) {
        return uniformColumnIds;
    }

    for (const [columnId, getComparableValue] of Object.entries(uniformityValueByColumn)) {
        const firstValue = getComparableValue(firstBacktest);

        if (backtests.every((backtest) => getComparableValue(backtest) === firstValue)) {
            uniformColumnIds.add(columnId as PortfolioBacktestColumnId);
        }
    }

    return uniformColumnIds;
};
